import { Input, Select, useTraversal } from '@guillotinaweb/react-gmi'
import { Switch, Tabs, message } from 'antd'
import React, { memo, useState } from 'react'
import { I18NField } from '../../../components/i18nfield'
import { ProductSelector } from '../../../components/product-selector'
import { frontThemes, getCdnUrl, setLangs } from '../../../consts'
import { MultiFileUpload } from '../product/partials/images'
import Arrow from './Arrow'

const Empty = () => null

const ImageWithArrows = ({
  imageUrl,
  moveUp,
  moveDown,
  isFirst,
  isLast,
  selected,
  lang,
  index,
  edit,
}) => {
  const arrowStyle = {
    position: 'absolute',
    top: '50%',
    transform: 'translateY(-50%)',
    zIndex: 1,
    backgroundColor: 'black',
    color: 'white',
    borderRadius: '50%',
    padding: '5px',
    cursor: 'pointer',
  }

  const arrowUpStyle = {
    ...arrowStyle,
    left: '10px', // Ajustar la posición lateral de la flecha hacia la izquierda
  }

  const arrowDownStyle = {
    ...arrowStyle,
    right: '10px', // Ajustar la posición lateral de la flecha hacia la derecha
  }

  return (
    <div
      className="image-container"
      style={{ position: 'relative', display: 'inline-block' }}
    >
      {!isFirst && (
        <Arrow direction="up" onClick={moveUp} style={arrowUpStyle} />
      )}
      <img src={imageUrl} alt="Image" />
      {!isLast && (
        <Arrow direction="down" onClick={moveDown} style={arrowDownStyle} />
      )}
      <button
        onClick={() => {
          const imagesCopy = [...selected.images[lang]]
          imagesCopy.splice(index, 1)
          edit('images', { ...selected.images, [lang]: imagesCopy })
        }}
        className="button-delete-image"
      >
        X
      </button>
    </div>
  )
}

export const ImagesForm = memo(({ selected, edit, langs, id }) => {
  const Ctx = useTraversal()
  const [uploading, setUploading] = useState(false)
  const [images, setImages] = useState(setLangs([]))

  const moveImageUp = (index, lang) => {
    if (index > 0 && index < selected.images[lang].length) {
      const imagesCopy = [...selected.images[lang]]
      const temp = imagesCopy[index]
      imagesCopy[index] = imagesCopy[index - 1]
      imagesCopy[index - 1] = temp
      edit('images', { ...selected.images, [lang]: imagesCopy })
    }
  }

  const moveImageDown = (index, lang) => {
    if (index >= 0 && index < selected.images[lang].length - 1) {
      const imagesCopy = [...selected.images[lang]]
      const temp = imagesCopy[index]
      imagesCopy[index] = imagesCopy[index + 1]
      imagesCopy[index + 1] = temp
      edit('images', { ...selected.images, [lang]: imagesCopy })
    }
  }

  function onUploadBannerMultipleImage(lang) {
    // Función para leer un archivo como un ArrayBuffer

    // Función para obtener las dimensiones de una imagen desde un ArrayBuffer
    function getImageDeviceTypeFromArrayBuffer(arrayBuffer) {
      return new Promise((resolve, reject) => {
        const blob = new Blob([arrayBuffer])
        const img = new Image()
        img.onload = () => {
          const deviceType = img.width > 768 ? 'desktop' : 'mobile'
          resolve(deviceType)
        }
        img.onerror = reject
        img.src = URL.createObjectURL(blob)
      })
    }

    return async (files) => {
      setUploading(true)
      let desktopImageUploaded = false
      try {
        const newImages = {
          desktop: '',
          link: '', // Aquí puedes definir la ruta del enlace según sea necesario
          mobile: '',
        }

        for (const file of files) {
          const deviceType = await getImageDeviceTypeFromArrayBuffer(file.data)
          const filename = `home/${Ctx.state.context['@name']}_desktop`
          const res = await Ctx.client.upload(Ctx.path, file, filename)

          if (res.status >= 300) {
            message.error('Error uploading image')
            setUploading(false)
            return
          }

          const { path } = await res.json()
          const src = `/${path}`

          if (deviceType === 'desktop') {
            newImages.desktop = src
            desktopImageUploaded = true
          } else {
            newImages.mobile = src
          }
        }

        // Si solo se ha subido una imagen de escritorio, usarla también para mobile
        if (desktopImageUploaded && !newImages.mobile) {
          newImages.mobile = newImages.desktop
        }

        const allImages = { ...selected.images } || images

        if (lang === 'es') {
          // Si el idioma es "ES", actualizamos las imágenes en todos los idiomas
          Object.keys(allImages).forEach((locale) => {
            if (Array.isArray(allImages[locale])) {
              allImages[locale].push(newImages)
            } else {
              allImages[locale] = [newImages]
            }
          })
        } else {
          // Para otros idiomas, solo actualizamos las imágenes del idioma seleccionado
          if (Array.isArray(allImages[lang])) {
            allImages[lang].push(newImages)
          } else {
            allImages[lang] = [newImages]
          }
        }

        setImages(allImages)
        edit('images', allImages)
        setUploading(false)
      } catch (error) {
        console.log('Error processing images:', error)
        message.error('Error processing images. Please try again.')
        setUploading(false)
      }
    }
  }

  const onChangeMultipleLinks = (value, lang, imageSrc) => {
    // Hacemos una copia profunda de selected.images
    const allImages = selected.images
      ? JSON.parse(JSON.stringify(selected.images))
      : {}

    let index = -1
    // Buscamos el índice del elemento que tiene el imageSrc dado
    allImages[lang].map((val, ind) => {
      // Aquí `val` representa el valor actual del elemento y `ind` es su índice
      // Compara `val` con el diccionario que se está modificando (imageSrc)
      if (JSON.stringify(val) === JSON.stringify(imageSrc)) {
        index = ind
      }
    })

    if (index !== -1) {
      // Si se encontró el índice, actualizamos el valor en la posición correspondiente
      allImages[lang][index]['link'] = value
      edit('images', allImages)
    } else {
      console.log('El elemento no se encontró en la lista de imágenes.')
    }
  }

  return (
    <I18NField key={langs.join()} langs={langs} Widget={Empty}>
      {(lang) => (
        <Tabs defaultActiveKey="1">
          <Tabs.TabPane tab="General" key={`general-${lang}`}>
            <>
              <label className="label">Images</label>
              <MultiFileUpload
                accept="image/*"
                disabled={uploading}
                onChange={onUploadBannerMultipleImage(lang)}
                label={uploading ? 'Uploading...' : 'Upload banner photos'}
              />
            </>
            {selected.images?.[lang].map((image, index) => {
              const isFirst = index === 0
              const isLast = index === selected.images[lang].length - 1

              return (
                <div key={index} style={{ position: 'relative' }}>
                  <ImageWithArrows
                    imageUrl={`${getCdnUrl()}${image.desktop}`}
                    moveUp={() => moveImageUp(index, lang)}
                    moveDown={() => moveImageDown(index, lang)}
                    isFirst={isFirst}
                    isLast={isLast}
                    selected={selected}
                    lang={lang}
                    index={index}
                    edit={edit}
                  />
                </div>
              )
            })}
            <label className="label mt10">Theme</label>
            <Select
              options={frontThemes.map((k) => ({ label: k, value: k }))}
              value={selected.theme}
              onChange={(e) => edit('theme', e.target.value)}
            />
          </Tabs.TabPane>

          <Tabs.TabPane tab="Mobile" key={`mobile-${lang}`}>
            {selected.images?.[lang].length !== 0 &&
              selected.images?.[lang].map((image, index) => {
                return (
                  <div key={image} style={{ position: 'relative' }}>
                    <button
                      onClick={() => {
                        const images = selected.images
                        images[lang].splice(index, 1)
                        edit('images', images)
                      }}
                      className="button-delete-image"
                    >
                      X
                    </button>
                    <img
                      src={`${getCdnUrl()}${image.mobile}`}
                      alt="Banner preview"
                    />
                  </div>
                )
              })}
            <label className="label mt10">Theme</label>
            <Select
              options={frontThemes.map((k) => ({ label: k, value: k }))}
              value={selected.theme}
              onChange={(e) => edit('theme', e.target.value)}
            />
          </Tabs.TabPane>
          <Tabs.TabPane tab="Links" key={`text-button-${lang}`}>
            <label className="label">Image Link</label>
            {selected.images[lang] &&
              selected.images[lang].map((value) => {
                return (
                  <div>
                    <label>{value.desktop}</label>
                    <Input
                      id={value.desktop}
                      name={value.desktop}
                      value={
                        value.link && value.link
                          ? decodeURIComponent(value.link)
                          : ''
                      }
                      onChange={(v) => {
                        onChangeMultipleLinks(v, lang, value)
                      }}
                    />
                  </div>
                )
              })}
          </Tabs.TabPane>
          {selected.id === 'product-image' && (
            <Tabs.TabPane tab="Products to display" key={`products-${lang}`}>
              <label className="label">Products</label>
              <ProductSelector
                id={selected.id}
                type="group"
                products={selected.query?.groups || []}
                onChange={(groups) => {
                  if (groups.length > 4) {
                    return message.error(
                      'This section can show a maximum of 4 products.'
                    )
                  }
                  edit('query', { groups })
                }}
              />
            </Tabs.TabPane>
          )}
          <Tabs.TabPane
            tab="Image properties"
            key={`image-properties-${lang}`}
            style={{
              display: 'flex',
              flexWrap: 'wrap',
              justifyContent:
                selected.id === 'image' ? 'space-around' : 'flex-start',
              alignItems: 'center',
            }}
          >
            <div>
              <label className="label mt10">Filter</label>
              <Select
                options={['no filter', 'clear', 'dark'].map((k) => ({
                  label: k,
                  value: k,
                }))}
                value={selected.filter ? selected.filter : 'no filter'}
                onChange={(e) => {
                  const v = e.target.value
                  edit('filter', v === 'no filter' ? undefined : v)
                }}
              />
              {selected.id === 'image' && (
                <>
                  <label className="label">Image repeat</label>
                  <Switch
                    defaultChecked={selected.imageRepeat}
                    onChange={(v) => edit('imageRepeat', v)}
                  />
                </>
              )}

              <label className="label">Image cover</label>
              <Switch
                defaultChecked={selected.imageCover}
                onChange={(v) => edit('imageCover', v)}
              />
              {selected.id === 'image' && (
                <>
                  <label className="label">Remove padding</label>
                  <Switch
                    defaultChecked={selected.removePadding}
                    onChange={(v) => edit('removePadding', v)}
                  />
                </>
              )}
            </div>
            {selected.id === 'image' && (
              <div>
                <label className="label">Width</label>
                <Select
                  options={['regular', 'big', 'full'].map((k) => ({
                    label: k,
                    value: k,
                  }))}
                  value={selected.widthSize}
                  onChange={(e) => edit('widthSize', e.target.value)}
                />

                <label className="label">Height (in pixels)</label>
                <Input
                  min={0}
                  type="number"
                  value={selected.height}
                  onChange={(v) => edit('height', parseFloat(v))}
                />
              </div>
            )}
          </Tabs.TabPane>
        </Tabs>
      )}
    </I18NField>
  )
})
